import React, { useState } from "react";
import styles from "./styles.module.scss";
import UserAvatar from "../UserAvatar";
import DropdownButton from "../DropdownButton";
import UserModel from "app/models/UserModel";
import { cls } from "app/utils";
import EditMemberModal from "app/routes/Groups/_components/EditMemberModal";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { useTranslation } from "react-i18next";
import { useActions } from "app/utils/hooks";
import { resendInvitation } from "app/actions/invitation";
import { toast } from "react-toastify";

interface Props {
  member: UserModel;
  role?: string | null;
  onRemoveMember?: (id: string, userName: string) => void;
  memberEditModal?: boolean;
  onMemberUpdate?: (member: UserModel) => void;
}

const GroupMember = ({
  member,
  role,
  onRemoveMember,
  onMemberUpdate,
}: Props) => {
  const groupMemberActions = useActions({
    resendInvitation,
  });
  const { t } = useTranslation();
  const { selectedGroup } = useSelector(
    (state: RootState) => state.groups,
    shallowEqual,
  );
  const [memberEditModal, setMemberEditModal] = useState(false);

  const memberModalHandler = () => {
    setMemberEditModal((prev) => !prev);
  };

  const resendInvitationHandler = (inviteId) => {
    groupMemberActions
      .resendInvitation({ id: inviteId })
      .then(() => {
        toast.success(t("Invitation resent"));
      })
      .catch(() => {
        toast.error(
          // t(
          //   "You can only send up to 1 invitation reminder to a person every 24 hours"
          // )
          t("Cannot resend this invitation, please try again later."),
        );
      });
  };

  const canManageMembers =
    !role &&
    selectedGroup?.permissions?.includes("manage_members") &&
    selectedGroup?.planPermissions?.includes("manage_members");

  const memberMenu = [
    {
      title: t("Resend Invitation"),
      onClick: (member: UserModel) => {
        resendInvitationHandler(member?.invitationId);
      },
      permission: member?.status === "pending" && canManageMembers,
    },
    {
      title: t("Edit Member"),
      onClick: () => {
        memberModalHandler();
      },
      permission: true,
    },
    {
      title: t("Remove From Group"),
      onClick: (member: UserModel) => {
        onRemoveMember(member?.id, member.name);
      },
      className: styles.delete_btn,
      permission: true,
    },
  ];

  const onMemberUpdateHandler = (user: UserModel) => {
    onMemberUpdate(user);
    memberModalHandler();
  };

  return (
    <div className={styles.member_container}>
      <div className={styles.member_wrapper}>
        <UserAvatar
          altName="member"
          srcName={member.avatar as string}
          userName={member.name}
          className={styles.avatar}
          sizeHeight={44}
          sizeWidth={44}
        />
        <div className={styles.member_info}>
          <div className={styles.username}>
            {member.name}{" "}
            {!member.email && (
              <b className={styles.error_text}>({t("Missing Email")})</b>
            )}
            {member.nickname && (
              <span className={styles.nickname_container}>
                {member.nickname}
              </span>
            )}
          </div>
          {role && <div className={styles.role}>{role}</div>}
        </div>
      </div>
      {canManageMembers && (
        <DropdownButton
          closeOnClick
          dropdownWrapperClass={styles.dropdown_wrapper}
          btnContent={<i className="bullets" />}
        >
          {memberMenu.map((item) => {
            if (!item.permission) {
              return null;
            }
            return (
              <div key={item.title} className={styles.container}>
                <button onClick={() => item.onClick(member)}>
                  <p className={cls(item.className)}>{item.title}</p>
                </button>
              </div>
            );
          })}
        </DropdownButton>
      )}
      {memberEditModal && (
        <EditMemberModal
          member={member}
          isOpen={memberEditModal}
          onClose={memberModalHandler}
          onMemberUpdate={onMemberUpdateHandler}
        />
      )}
    </div>
  );
};

export default GroupMember;
